import React from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from '@mui/material';
import {
  useAddEntitlement,
  useInternalCatalogTypes,
} from '../../data/useInternalAdmin';

const ENTITLEMENT_TYPES = [
  { value: 'feature', label: 'Feature' },
  { value: 'limit', label: 'Connector Limit' },
];

const KNOWN_FEATURE_KEYS = [{ value: 'canvas', label: 'canvas' }];

const ENTITY_TYPE_OPTIONS = [
  {
    value: 'integration',
    label: 'Connection',
    entitlementPrefix: 'connection',
  },
  { value: 'processor', label: 'Processor', entitlementPrefix: 'processor' },
];

interface AddEntitlementDialogProps {
  open: boolean;
  onClose: () => void;
  accountId: string | undefined;
}

const AddEntitlementDialog: React.FC<AddEntitlementDialogProps> = ({
  open,
  onClose,
  accountId,
}) => {
  const addEntitlement = useAddEntitlement(accountId);
  const [entitlementType, setEntitlementType] = React.useState('feature');
  const [entityType, setEntityType] = React.useState('integration');
  const [typeName, setTypeName] = React.useState('');
  const [entitlementKey, setEntitlementKey] = React.useState('');
  const [value, setValue] = React.useState('');

  const catalogTypes = useInternalCatalogTypes(entityType);

  const entityOption = ENTITY_TYPE_OPTIONS.find((o) => o.value === entityType);
  const entitlementPrefix = entityOption?.entitlementPrefix || 'connection';

  const handleClose = () => {
    setEntitlementType('feature');
    setEntityType('integration');
    setTypeName('');
    setEntitlementKey('');
    setValue('');
    onClose();
  };

  const handleSubmit = async () => {
    if (entitlementType === 'feature') {
      await addEntitlement.mutateAsync({
        entitlement_type: 'feature',
        entitlement_key: entitlementKey,
        value: null,
      });
    } else {
      const key = `${entitlementPrefix}:${typeName}`;
      await addEntitlement.mutateAsync({
        entitlement_type: 'limit',
        entitlement_key: key,
        value: value,
      });
    }

    handleClose();
  };

  const isValid =
    entitlementType === 'feature'
      ? entitlementKey.length > 0
      : typeName.length > 0 && value.length > 0;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Entitlement</DialogTitle>
      <DialogContent
        sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}
      >
        <TextField
          select
          label="Type"
          value={entitlementType}
          onChange={(e) => {
            setEntitlementType(e.target.value);
            setEntitlementKey('');
            setTypeName('');
            setValue('');
          }}
          fullWidth
          margin="dense"
        >
          {ENTITLEMENT_TYPES.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>

        {entitlementType === 'feature' ? (
          <TextField
            select
            label="Feature Key"
            value={entitlementKey}
            onChange={(e) => setEntitlementKey(e.target.value)}
            fullWidth
            margin="dense"
          >
            {KNOWN_FEATURE_KEYS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
        ) : (
          <>
            <TextField
              select
              label="Entity Type"
              value={entityType}
              onChange={(e) => {
                setEntityType(e.target.value);
                setTypeName('');
              }}
              fullWidth
              margin="dense"
            >
              {ENTITY_TYPE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Type Name"
              value={typeName}
              onChange={(e) => setTypeName(e.target.value)}
              fullWidth
              margin="dense"
              disabled={catalogTypes.isLoading}
              InputProps={{
                endAdornment: catalogTypes.isLoading ? (
                  <CircularProgress size={20} sx={{ mr: 2 }} />
                ) : undefined,
              }}
            >
              {(catalogTypes.data || []).map((ct) => (
                <MenuItem key={ct.name} value={ct.name}>
                  {ct.title} ({ct.name})
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Instance Limit"
              type="number"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              fullWidth
              margin="dense"
              required
              helperText="Required. Maximum number of instances allowed."
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!isValid || addEntitlement.isPending}
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddEntitlementDialog;
